import { useAuthStore } from '../stores/authStore'
import type {
  GmailThread,
  GmailMessage,
  EmailThreadSummary,
  EmailMessage,
  GmailHeader,
  GmailMessagePart,
} from '../types'

const GMAIL_API = 'https://www.googleapis.com/gmail/v1/users/me'

class GmailService {
  private getAccessToken(): string {
    const token = useAuthStore.getState().accessToken
    if (!token) throw new Error('Not authenticated')
    return token
  }

  private async request<T>(url: string, options: RequestInit = {}, isRetry = false): Promise<T> {
    const token = this.getAccessToken()
    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    })

    // Handle token expiration - attempt refresh and retry once
    if (response.status === 401 && !isRetry) {
      console.log('[ SYSTEM: TOKEN EXPIRED. ATTEMPTING REFRESH... ]')
      const refreshed = await useAuthStore.getState().refreshToken()
      if (refreshed) {
        return this.request<T>(url, options, true)
      }
      throw new Error('Session expired. Please sign in again.')
    }

    if (response.status === 403) {
      throw new Error('Gmail access not granted. Please connect Gmail.')
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error?.message || `Gmail request failed: ${response.status}`)
    }

    return response.json()
  }

  async fetchThreadsForEmail(
    contactEmail: string,
    maxResults = 50
  ): Promise<EmailThreadSummary[]> {
    const query = encodeURIComponent(`from:${contactEmail} OR to:${contactEmail}`)
    const oneYearAgo = new Date()
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
    const afterDate = Math.floor(oneYearAgo.getTime() / 1000)
    
    const url = `${GMAIL_API}/threads?q=${query}+after:${afterDate}&maxResults=${maxResults}`
    
    const response = await this.request<{
      threads?: Array<{ id: string; snippet: string; historyId: string }>
      resultSizeEstimate: number
    }>(url)

    if (!response.threads || response.threads.length === 0) {
      return []
    }

    const threadSummaries = await Promise.all(
      response.threads.slice(0, maxResults).map(async (thread) => {
        try {
          return await this.getThreadSummary(thread.id)
        } catch {
          return null
        }
      })
    )

    return threadSummaries
      .filter((t): t is EmailThreadSummary => t !== null)
      .sort((a, b) => b.lastMessageDate.getTime() - a.lastMessageDate.getTime())
  }

  async getThreadSummary(threadId: string): Promise<EmailThreadSummary> {
    const thread = await this.request<GmailThread>(
      `${GMAIL_API}/threads/${threadId}?format=metadata&metadataHeaders=From&metadataHeaders=To&metadataHeaders=Subject&metadataHeaders=Date`
    )

    const messages = thread.messages || []
    const lastMessage = messages[messages.length - 1]
    const firstMessage = messages[0]

    const getHeader = (msg: GmailMessage, name: string): string => {
      const header = msg.payload.headers.find(
        (h) => h.name.toLowerCase() === name.toLowerCase()
      )
      return header?.value || ''
    }

    const participants = new Set<string>()
    messages.forEach((msg) => {
      const from = getHeader(msg, 'From')
      const to = getHeader(msg, 'To')
      if (from) participants.add(this.parseEmailAddress(from))
      to.split(',').forEach((addr) => {
        const parsed = this.parseEmailAddress(addr.trim())
        if (parsed) participants.add(parsed)
      })
    })

    const isUnread = lastMessage?.labelIds?.includes('UNREAD') || false

    return {
      id: thread.id,
      subject: firstMessage ? getHeader(firstMessage, 'Subject') || '(No subject)' : '(No subject)',
      participants: Array.from(participants),
      snippet: thread.snippet || lastMessage?.snippet || '',
      lastMessageDate: new Date(parseInt(lastMessage?.internalDate || '0', 10)),
      messageCount: messages.length,
      isUnread,
    }
  }

  async getFullThread(threadId: string): Promise<EmailMessage[]> {
    const thread = await this.request<GmailThread>(
      `${GMAIL_API}/threads/${threadId}?format=full`
    )

    return (thread.messages || []).map((msg) => this.parseMessage(msg))
  }

  private parseMessage(msg: GmailMessage): EmailMessage {
    const getHeader = (headers: GmailHeader[], name: string): string => {
      const header = headers.find((h) => h.name.toLowerCase() === name.toLowerCase())
      return header?.value || ''
    }

    const parseAddressList = (value: string): string[] => {
      if (!value) return []
      return value.split(',').map((addr) => addr.trim()).filter(Boolean)
    }

    const headers = msg.payload.headers
    const { bodyText, bodyHtml } = this.extractBody(msg.payload)

    return {
      id: msg.id,
      threadId: msg.threadId,
      from: getHeader(headers, 'From'),
      to: parseAddressList(getHeader(headers, 'To')),
      cc: parseAddressList(getHeader(headers, 'Cc')),
      subject: getHeader(headers, 'Subject') || '(No subject)',
      date: new Date(parseInt(msg.internalDate, 10)),
      snippet: msg.snippet,
      bodyText,
      bodyHtml,
    }
  }

  private extractBody(payload: GmailMessage['payload']): {
    bodyText: string
    bodyHtml?: string
  } {
    let bodyText = ''
    let bodyHtml: string | undefined

    const decodeBody = (data?: string): string => {
      if (!data) return ''
      try {
        return decodeURIComponent(
          atob(data.replace(/-/g, '+').replace(/_/g, '/'))
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        )
      } catch {
        return ''
      }
    }

    const extractFromParts = (parts: GmailMessagePart[]): void => {
      for (const part of parts) {
        if (part.mimeType === 'text/plain' && part.body?.data) {
          bodyText = decodeBody(part.body.data)
        } else if (part.mimeType === 'text/html' && part.body?.data) {
          bodyHtml = decodeBody(part.body.data)
        } else if (part.parts) {
          extractFromParts(part.parts)
        }
      }
    }

    if (payload.body?.data) {
      if (payload.mimeType === 'text/plain') {
        bodyText = decodeBody(payload.body.data)
      } else if (payload.mimeType === 'text/html') {
        bodyHtml = decodeBody(payload.body.data)
      }
    }

    if (payload.parts) {
      extractFromParts(payload.parts)
    }

    if (!bodyText && bodyHtml) {
      bodyText = bodyHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    }

    return { bodyText, bodyHtml }
  }

  private parseEmailAddress(raw: string): string {
    const match = raw.match(/<([^>]+)>/)
    if (match && match[1]) return match[1].toLowerCase()
    return raw.toLowerCase().trim()
  }

  getGmailComposeUrl(params: {
    to?: string
    subject?: string
    body?: string
  }): string {
    const base = 'https://mail.google.com/mail/?view=cm&fs=1'
    const queryParams: string[] = []

    if (params.to) {
      queryParams.push(`to=${encodeURIComponent(params.to)}`)
    }
    if (params.subject) {
      queryParams.push(`su=${encodeURIComponent(params.subject)}`)
    }
    if (params.body) {
      queryParams.push(`body=${encodeURIComponent(params.body)}`)
    }

    return queryParams.length > 0 ? `${base}&${queryParams.join('&')}` : base
  }

  getGmailThreadUrl(threadId: string): string {
    return `https://mail.google.com/mail/u/0/#inbox/${threadId}`
  }
}

export const gmailService = new GmailService()
